import React from 'react';
import {
  X, Activity, MessageSquare, MessageCircle, Plus,
  ArrowRight, PoundSterling, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const EVENT_CFG = {
  created: { Icon: Plus, color: 'bg-emerald-100 text-emerald-700' },
  note: { Icon: MessageCircle, color: 'bg-blue-100 text-blue-700' },
  status_change: { Icon: ArrowRight, color: 'bg-violet-100 text-violet-700' },
  quote: { Icon: PoundSterling, color: 'bg-amber-100 text-amber-700' },
  material_quote: { Icon: FileText, color: 'bg-amber-100 text-amber-700' },
};

const fmtTime = (ts) =>
  ts
    ? new Date(ts).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

/**
 * Lead Activity modal — shows the timeline tab (created / notes / status
 * changes / quotes) and a notes-only tab. Lets staff/installers append a new
 * note at the bottom.
 */
export default function CallLogModal({
  open,
  lead,
  activityTab,
  setActivityTab,
  activityEvents,
  loadingActivity,
  newCallNote,
  setNewCallNote,
  addingCallLog,
  onClose,
  onAddNote,
}) {
  if (!open || !lead) return null;

  const renderTimeline = () => {
    if (activityEvents.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No activity yet</p>
          <p className="text-sm">Add your first note below — it&apos;ll appear here.</p>
        </div>
      );
    }
    return (
      <ol className="relative border-l-2 border-gray-200 ml-3 space-y-4">
        {activityEvents.map((ev, idx) => {
          const cfg = EVENT_CFG[ev.type] || { Icon: Activity, color: 'bg-gray-100 text-gray-700' };
          return (
            <li
              key={ev.id || `${ev.type}-${ev.timestamp}-${idx}`}
              className="pl-6 relative"
              data-testid={`activity-event-${ev.type}-${idx}`}
            >
              <span className={`absolute -left-4 flex items-center justify-center w-7 h-7 rounded-full ${cfg.color} border-2 border-white`}>
                <cfg.Icon className="w-3.5 h-3.5" />
              </span>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-semibold text-gray-700">{ev.user_name}</span>
                <span className="text-[10px] text-gray-400">{fmtTime(ev.timestamp)}</span>
              </div>
              <p className="text-sm text-gray-700 mt-0.5">{ev.summary}</p>
              {ev.type === 'status_change' && (
                <div className="flex items-center gap-1 mt-1 text-[10px]">
                  {ev.from_status && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{ev.from_status}</span>
                  )}
                  <ArrowRight className="w-3 h-3 text-gray-400" />
                  <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">{ev.to_status}</span>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    );
  };

  const renderNotesOnly = () => {
    const logs = lead.call_logs || [];
    if (logs.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No notes yet</p>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {logs.slice().reverse().map((log, idx) => (
          <div key={log.id || idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">{log.user_name}</span>
              <span className="text-xs text-gray-400">{fmtTime(log.timestamp)}</span>
            </div>
            <p className="text-gray-600 text-sm">{log.note}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#27292E]">Lead Activity</h2>
            <p className="text-sm text-gray-500">{lead.customer_name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 border-b border-gray-100 flex gap-1">
          {[
            ['timeline', 'Timeline', Activity],
            ['notes', 'Notes only', MessageSquare],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              data-testid={`activity-tab-${key}`}
              onClick={() => setActivityTab(key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activityTab === key
                  ? 'border-[#2F5E96] text-[#2F5E96]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Activity feed */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingActivity ? (
            <div className="text-center text-gray-400 py-10">Loading…</div>
          ) : activityTab === 'timeline' ? (
            renderTimeline()
          ) : (
            renderNotesOnly()
          )}
        </div>

        {/* Add Note Form */}
        <div className="p-6 border-t border-gray-100 bg-gray-50">
          <Label className="text-sm font-medium mb-2 block">Add Note</Label>
          <div className="flex gap-2">
            <textarea
              value={newCallNote}
              onChange={(e) => setNewCallNote(e.target.value)}
              placeholder="Enter call notes, discussion points, next steps..."
              className="flex-1 border-2 rounded-lg p-3 min-h-[80px] text-sm resize-none"
            />
          </div>
          <Button
            onClick={onAddNote}
            disabled={!newCallNote.trim() || addingCallLog}
            className="mt-3 w-full bg-[#006400] hover:bg-[#004d00] text-white"
          >
            {addingCallLog ? 'Adding...' : 'Add Note'}
          </Button>
        </div>
      </div>
    </div>
  );
}
